import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { fileToCompressedDataUrl } from "../hooks/useImages";

const SLOT_LABELS = {
  hero: "Foto principale (Hero)",
  about: "Foto sezione 'Storia'",
  menu_dish1: "Piatto menu #1",
  menu_dish2: "Piatto menu #2",
};

const SLOT_ORDER = ["hero", "about", "menu_dish1", "menu_dish2"];

const SingleSlotCard = ({ slot, image, onUpload, onDelete, busy }) => {
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo immagini");
      return;
    }
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      await onUpload(slot, { data_url: dataUrl, filename: file.name, alt: SLOT_LABELS[slot] });
    } catch (err) {
      toast.error("Errore caricamento immagine");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`slot-card-${slot}`}
      className="bg-[color:var(--ivory-2)] border border-[color:var(--line)] p-4"
    >
      <p className="overline text-[color:var(--wine)] mb-3">{SLOT_LABELS[slot]}</p>
      <div className="aspect-[4/3] bg-[color:var(--ivory-3)] overflow-hidden mb-3">
        {image ? (
          <img src={image.src} alt={image.alt || slot} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--ink-3)] text-sm">
            Nessuna foto
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid={`upload-${slot}`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn-primary flex-1 disabled:opacity-50"
          style={{ padding: "0.6rem 0.9rem", fontSize: "0.7rem" }}
        >
          <Upload size={12} strokeWidth={1.6} />
          {image ? "Sostituisci" : "Carica"}
        </button>
        {image && (
          <button
            type="button"
            onClick={() => onDelete(image.id)}
            disabled={busy}
            className="p-2 text-[color:var(--ink-3)] hover:text-[color:var(--wine)]"
            title="Elimina"
            data-testid={`delete-${slot}`}
          >
            <Trash2 size={16} strokeWidth={1.4} />
          </button>
        )}
      </div>
    </div>
  );
};

const GalleryGrid = ({ images, onUpload, onDelete, onReorder, busy }) => {
  const inputRef = useRef(null);
  const [altMap, setAltMap] = useState({});

  useEffect(() => {
    const m = {};
    for (const img of images) m[img.id] = img.alt || "";
    setAltMap(m);
  }, [images]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const dataUrl = await fileToCompressedDataUrl(file);
        await onUpload("gallery", {
          data_url: dataUrl,
          filename: file.name,
          alt: file.name.replace(/\.[^.]+$/, ""),
          order: (images[images.length - 1]?.order || 0) + 1,
        });
      } catch (err) {
        toast.error(`Errore: ${file.name}`);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const reordered = [...images];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    onReorder(reordered);
  };
  const moveDown = (idx) => {
    if (idx >= images.length - 1) return;
    const reordered = [...images];
    [reordered[idx], reordered[idx + 1]] = [reordered[idx + 1], reordered[idx]];
    onReorder(reordered);
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="overline text-[color:var(--wine)]">Galleria · {images.length} foto</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid="gallery-upload-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="gallery-add"
          className="btn-primary disabled:opacity-50"
          style={{ padding: "0.6rem 1rem" }}
        >
          <Plus size={14} strokeWidth={1.6} />
          Aggiungi foto
        </button>
      </div>
      {images.length === 0 ? (
        <p className="text-[color:var(--ink-3)] italic text-sm">Nessuna foto in galleria.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, idx) => (
            <div
              key={img.id}
              data-testid={`gallery-item-${idx}`}
              className="bg-[color:var(--ivory-2)] border border-[color:var(--line)] p-3"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[color:var(--ivory-3)] mb-2">
                <img src={img.src} alt={img.alt || ""} className="w-full h-full object-cover" />
              </div>
              <div className="flex items-center justify-between text-[color:var(--ink-3)] text-xs">
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveUp(idx)} className="hover:text-[color:var(--wine)] px-1">▲</button>
                  <button type="button" onClick={() => moveDown(idx)} className="hover:text-[color:var(--wine)] px-1">▼</button>
                </div>
                <span className="overline text-[0.55rem]">#{idx + 1}</span>
                <button
                  type="button"
                  onClick={() => onDelete(img.id)}
                  disabled={busy}
                  data-testid={`gallery-delete-${idx}`}
                  className="hover:text-[color:var(--wine)] p-1"
                  title="Elimina"
                >
                  <Trash2 size={14} strokeWidth={1.4} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ImagesManager = ({ authedAxios }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await authedAxios().get("/images");
      setImages(res.data || []);
    } catch {
      toast.error("Errore caricamento foto");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (slot, payload) => {
    setBusy(true);
    try {
      await authedAxios().post("/images", { slot, ...payload });
      toast.success("Foto caricata");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore caricamento";
      toast.error(typeof msg === "string" ? msg : "Errore");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    setBusy(true);
    try {
      await authedAxios().delete(`/images/${id}`);
      toast.success("Foto eliminata");
      await load();
    } catch {
      toast.error("Errore eliminazione");
    } finally {
      setBusy(false);
    }
  };

  const reorderGallery = async (newOrder) => {
    setBusy(true);
    try {
      // PUT each image with updated order
      const ax = authedAxios();
      await Promise.all(
        newOrder.map((img, idx) =>
          ax.put(`/images/${img.id}`, {
            slot: "gallery",
            order: idx + 1,
            data_url: null,
            external_url: img.src.startsWith("http") ? img.src : null,
            alt: img.alt || "",
          })
        )
      );
      await load();
    } catch {
      toast.error("Errore riordinamento");
    } finally {
      setBusy(false);
    }
  };

  const single = (slot) => images.find((i) => i.slot === slot);
  const gallery = images.filter((i) => i.slot === "gallery");

  return (
    <section data-testid="admin-images-section" className="mb-14">
      <p className="overline text-[color:var(--wine)] flex items-center mb-6">
        <span className="section-line"></span>
        Foto del locale
      </p>
      <h2 className="font-serif-display text-3xl mb-8">Immagini.</h2>

      {loading ? (
        <p className="text-[color:var(--ink-3)]">Caricamento...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {SLOT_ORDER.map((slot) => (
              <SingleSlotCard
                key={slot}
                slot={slot}
                image={single(slot)}
                onUpload={upload}
                onDelete={remove}
                busy={busy}
              />
            ))}
          </div>

          <GalleryGrid
            images={gallery}
            onUpload={upload}
            onDelete={remove}
            onReorder={reorderGallery}
            busy={busy}
          />
        </>
      )}
      <p className="mt-6 text-xs text-[color:var(--ink-3)] italic">
        Le foto vengono ridimensionate automaticamente a max 1920px e compresse per non appesantire il sito.
      </p>
    </section>
  );
};

export default ImagesManager;
